import {
  AgentEnvironment,
  DiscoveryStageId,
  DiscoveryStageProgress,
  DiscoveryStageStatus,
  OnboardingLifecycle,
} from "@/api/temp/AgentEstateService";

/**
 * The onboarding run is a pure function of wall-clock time since
 * `environment.startedAt` - nothing ticks, nothing is written. Every read
 * derives the same snapshot from the same clock, so the estate list, the
 * category counts and the progress panel can never disagree with each other.
 */

/**
 * Total wall-clock budget for one discovery run - the single knob for how long
 * onboarding appears to take. Every stage window below is a fraction of this,
 * so changing it rescales the whole run rather than desyncing the stages.
 *
 * Kept short on purpose: this is a demo, and seven stages that take a minute
 * to walk through is a minute of the audience waiting rather than watching.
 */
export const DISCOVERY_DURATION_MS = 24_000;

export interface StageWindow<Id extends string = DiscoveryStageId> {
  id: Id;
  /** Fraction of DISCOVERY_DURATION_MS at which the stage starts. */
  start: number;
  /** Fraction at which it finishes. Equal to `start` means instantaneous. */
  end: number;
}

/**
 * The stages the administrator sees, in plain language. Discovery already ran
 * inside the wizard, so the run opens by importing the selected agents rather
 * than finding them again. Two stages are load-bearing rather than decorative:
 *
 *  - IMPORTING grows the estate: `discoveredCount` is derived from it, so the
 *    cards and the list fill up as it runs.
 *  - CLASSIFYING decides how much of the estate has a security status at all:
 *    `classifiedCount` is derived from it, and the gap between the two is
 *    exactly the set of NOT_EVALUATED agents.
 *
 * Stages overlap deliberately, which is what makes the panel read as
 * concurrent work rather than a queue. CLASSIFYING both starts and ends after
 * IMPORTING, so classified can never outrun imported.
 */
export const STAGE_WINDOWS: readonly StageWindow[] = [
  { id: "IMPORTING", start: 0, end: 0.12 },
  { id: "LINKING_MCP", start: 0.04, end: 0.2 },
  { id: "RESOLVING_IDENTITY", start: 0.1, end: 0.5 },
  { id: "METADATA", start: 0.15, end: 0.6 },
  { id: "ANALYZING", start: 0.3, end: 0.75 },
  { id: "CLASSIFYING", start: 0.45, end: 0.85 },
  { id: "POLICY_GAPS", start: 0.6, end: 0.92 },
  { id: "RECOMMENDATIONS", start: 0.75, end: 1 },
];

const clamp01 = (value: number) => Math.min(1, Math.max(0, value));

/**
 * Fraction of `stage` completed at `elapsedMs`. Instant stages are 1 at t=0.
 * `durationMs` is the run the windows are fractions of - onboarding by default.
 */
export function stageRatio(
  stage: StageWindow<string>,
  elapsedMs: number,
  durationMs = DISCOVERY_DURATION_MS,
): number {
  const startMs = stage.start * durationMs;
  const endMs = stage.end * durationMs;
  if (endMs <= startMs) return elapsedMs >= startMs ? 1 : 0;
  return clamp01((elapsedMs - startMs) / (endMs - startMs));
}

export function stageStatus(ratio: number): DiscoveryStageStatus {
  if (ratio >= 1) return "COMPLETE";
  return ratio > 0 ? "RUNNING" : "PENDING";
}

export function elapsedMsFor(
  environment: AgentEnvironment,
  now = Date.now(),
): number {
  return Math.max(0, now - new Date(environment.startedAt).getTime());
}

export function lifecycleFor(elapsedMs: number): OnboardingLifecycle {
  return elapsedMs < DISCOVERY_DURATION_MS ? "IN_PROGRESS" : "COMPLETE";
}

/**
 * Per-stage progress. Every stage counts agents except LINKING_MCP, which
 * counts the MCP Services those agents reference.
 */
export function stageProgress(
  totalAgents: number,
  elapsedMs: number,
  totalMcpServices = totalAgents,
): DiscoveryStageProgress[] {
  return STAGE_WINDOWS.map(stage => {
    const ratio = stageRatio(stage, elapsedMs);
    const total = stage.id === "LINKING_MCP" ? totalMcpServices : totalAgents;
    return {
      id: stage.id,
      status: stageStatus(ratio),
      value: Math.round(total * ratio),
      total,
    };
  });
}

const windowFor = (id: DiscoveryStageId): StageWindow =>
  STAGE_WINDOWS.find(stage => stage.id === id)!;

/**
 * How many selected agents have been imported so far. This single scalar is the whole
 * estate: agents past it simply are not there yet, which is why there is no
 * placeholder row to explain. Once the run lands it equals the total.
 */
export function discoveredCount(
  totalAgents: number,
  elapsedMs: number,
): number {
  if (lifecycleFor(elapsedMs) === "COMPLETE") return totalAgents;
  return Math.round(totalAgents * stageRatio(windowFor("IMPORTING"), elapsedMs));
}

/**
 * How many discovered agents have been classified, and so have a security
 * status. Discovered-but-not-classified agents report NOT_EVALUATED - which is
 * the honest answer while the run is mid-flight, and is why the settled estate
 * needs no permanent NOT_EVALUATED cohort to make the status reachable.
 *
 * Clamped to `discoveredCount` as belt-and-braces; the stage windows already
 * guarantee it.
 */
export function classifiedCount(totalAgents: number, elapsedMs: number): number {
  if (lifecycleFor(elapsedMs) === "COMPLETE") return totalAgents;
  return Math.min(
    discoveredCount(totalAgents, elapsedMs),
    Math.round(totalAgents * stageRatio(windowFor("CLASSIFYING"), elapsedMs)),
  );
}

/**
 * The run stopped at `failedId`: stages before it finished, it failed with
 * whatever it had reached, and everything after never started.
 */
export function failedStageProgress(
  totalAgents: number,
  failedId: DiscoveryStageId,
  totalMcpServices = totalAgents,
): DiscoveryStageProgress[] {
  const failedIndex = STAGE_WINDOWS.findIndex(stage => stage.id === failedId);
  return STAGE_WINDOWS.map((stage, index) => {
    const total = stage.id === "LINKING_MCP" ? totalMcpServices : totalAgents;
    if (index < failedIndex)
      return { id: stage.id, status: "COMPLETE", value: total, total };
    if (index === failedIndex)
      return {
        id: stage.id,
        status: "FAILED",
        value: Math.round(total * 0.4),
        total,
      };
    return { id: stage.id, status: "PENDING", value: 0, total };
  });
}
